"use client";

//import { headers } from "next/headers";

import { Button } from "@/components/ui/button";
import { authClient } from "@/lib/auth/client";
import { normalizeDestination } from "@/lib/deep-linking";
import { cn, callbackUrlIsSafe } from "@/lib/utils-client";

// Icon components
const Google = (props: React.ComponentProps<"svg">) => (
  <svg
    width="1em"
    height="1em"
    viewBox="0 0 256 262"
    xmlns="http://www.w3.org/2000/svg"
    preserveAspectRatio="xMidYMid"
    {...props}
  >
    <path
      d="M255.878 133.451c0-10.734-.871-18.567-2.756-26.69H130.55v48.448h71.947c-1.45 12.04-9.283 30.172-26.69 42.356l-.244 1.622 38.755 30.023 2.685.268c24.659-22.774 38.875-56.282 38.875-96.027"
      fill="#4285F4"
    />
    <path
      d="M130.55 261.1c35.248 0 64.839-11.605 86.453-31.622l-41.196-31.913c-11.024 7.688-25.82 13.055-45.257 13.055-34.523 0-63.824-22.773-74.269-54.25l-1.531.13-40.298 31.187-.527 1.465C35.393 231.798 79.49 261.1 130.55 261.1"
      fill="#34A853"
    />
    <path
      d="M56.281 156.37c-2.756-8.123-4.351-16.827-4.351-25.82 0-8.994 1.595-17.697 4.206-25.82l-.073-1.73L15.26 71.312l-1.335.635C5.077 89.644 0 109.517 0 130.55s5.077 40.905 13.925 58.602l42.356-32.782"
      fill="#FBBC05"
    />
    <path
      d="M130.55 50.479c24.514 0 41.05 10.589 50.479 19.438l36.844-35.974C195.245 12.91 165.798 0 130.55 0 79.49 0 35.393 29.301 13.925 71.947l42.211 32.783c10.59-31.477 39.891-54.251 74.414-54.251"
      fill="#EB4335"
    />
  </svg>
);

const Apple = (props: React.ComponentProps<"svg">) => (
  <svg
    width="1em"
    height="1em"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"
      fill="currentColor"
    />
  </svg>
);

const Meta = (props: React.ComponentProps<"svg">) => (
  <svg
    width="1em"
    height="1em"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
      fill="#1877F2"
    />
  </svg>
);

interface SignInComponentProps {
  /**
   * Full destination URL (path + search + hash) to redirect to after signin
   * Preferred method using type-safe deep linking (ADR-018)
   * @example "/listing/abc123?ref=email#place-bid"
   */
  destination?: string;
  title?: string;
  variant?: "page" | "modal";
}

export function SignInComponent({
  destination,
  title,
  variant = "page",
}: SignInComponentProps) {
  const isModal = variant === "modal";

  // Set defaults based on variant if not provided
  const defaultTitle = isModal ? "Welcome to ApartmentDibs" : "";

  const finalTitle = title ?? defaultTitle;
  if (typeof window !== "undefined") {
    console.debug({
      windowLocation: window.location,
      windowLocationHref: window.location.href,
      windowLocationPathname: window.location.pathname,
      windowLocationSearch: window.location.search,
      windowLocationHash: window.location.hash,
      windowLocationHost: window.location.host,
      windowLocationHostname: window.location.hostname,
      windowLocationPort: window.location.port,
      windowLocationProtocol: window.location.protocol,
      windowLocationOrigin: window.location.origin,
    });
  }

  // Normalize destination from any encoding format (raw, URL-encoded, or base64)
  // If no destination provided, automatically uses current browser location
  const normalizedDestination = normalizeDestination(destination);

  // Validate the normalized destination for security
  const safeDestination = callbackUrlIsSafe(normalizedDestination)
    ? normalizedDestination
    : "/";

  // Parse destination into components
  const parts = safeDestination.split("#");
  const pathAndSearch = parts[0] || "";
  const hash = parts[1];

  const pathParts = pathAndSearch.split("?");
  const path = pathParts[0] || "/";
  const search = pathParts[1];

  const finalCallbackPath = path;
  const finalCallbackHash = hash ? `#${hash}` : "";
  const finalCallbackSearch = search ? `?${search}` : "";

  // ALWAYS use the window.location.origin to ensure the callback URL is correct for
  // oauth proxy to work correctly. It's the only thing that actually knows what
  // URL we're actually looking at.
  const finalCallbackUrl = `${window.location.origin}${finalCallbackPath}${finalCallbackSearch}${finalCallbackHash}`;

  // Get the last login method as a hint for the user
  const lastLoginMethod = authClient.getLastUsedLoginMethod();

  const containerSpacing = isModal ? "space-y-4" : "space-y-6";
  const headerTitleSize = isModal ? "text-sm" : "text-lg";
  const buttonSpacing = "space-y-2";

  return (
    <div className={containerSpacing}>
      {/* Header */}
      {finalTitle && (
        <div className="pt-1.75 text-center">
          <h1 className={cn("font-semibold", headerTitleSize)}>{finalTitle}</h1>
        </div>
      )}

      {lastLoginMethod && (
        <div
          className={cn(
            "mb-5 text-center text-sm text-primary",
            isModal ? "" : "",
          )}
        >
          You last logged in with{" "}
          {lastLoginMethod.charAt(0).toUpperCase() + lastLoginMethod.slice(1)}.
        </div>
      )}

      {/* Social Login Buttons */}
      <div className={buttonSpacing}>
        <Button
          variant="outline"
          className="h-[48px] w-full justify-center gap-3 text-sm"
          onClick={async () => {
            await authClient.signIn.social({
              provider: "google",
              callbackURL: finalCallbackUrl,
            });
            await authClient.getSession();
          }}
        >
          Continue with Google
          <Google className="h-4 w-4" />
        </Button>

        <Button
          variant="outline"
          className="h-[48px] w-full cursor-not-allowed justify-center gap-3 text-sm opacity-50"
          disabled
        >
          Continue with Apple
          <Apple className="h-4 w-4" />
        </Button>

        <Button
          variant="outline"
          className="h-[48px] w-full cursor-not-allowed justify-center gap-3 text-sm opacity-50"
          disabled
        >
          Continue with Facebook
          <Meta className="h-4 w-4" />
        </Button>

        {/* Terms */}
        <div
          className={cn(
            "mt-5 text-center text-sm text-muted-foreground",
            isModal ? "mt-4" : "mt-8",
          )}
        >
          <a
            href="https://www.apartmentdibs.com/legal/terms-and-conditions"
            className="hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            Terms of Service
          </a>
          {" • "}
          <a
            href="https://www.apartmentdibs.com/legal/privacy-policy"
            className="hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </a>
        </div>
      </div>
    </div>
  );
}
